import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { SelectField } from '../../../../components/ui/SelectField';
import type { ReportingDatePreset } from '../../../../types/reporting';
import type { DateRange } from '../../../../lib/reportingDateRange';

// CLIENT PORTAL Phase 1 — the period control.
//
// ⚠️ THE DEFAULT IS SET BY THE CALLER'S INITIAL STATE, NOT HERE, and since
// 2026-09-02 it is resolved by `resolveDefaultPortalPreset` — this month once it
// holds three complete days, last month before that. See reportPeriod.ts.
//
// ⚠️ A DELIBERATELY SHORT PRESET LIST. The staff dashboards offer fourteen,
// including today / yesterday / this-week, which are operational questions an AM
// asks while managing a campaign. A client is asking about a reporting period,
// so the list is the handful that means something to them; `custom` covers the
// rest. Presets resolve through the SHARED `presetToRange`, so "last month"
// cannot come to mean something different here than it does internally.
//
// ⚠️⚠️ `past_7`, `past_14` and `past_60` ALREADY EXISTED — in
// `ReportingDatePreset` and with their own cases in `presetToRange`. Adding a
// second resolution path for them would be the failure this project has already
// recorded: two surfaces disagreeing about what a window means is invisible
// until somebody compares two reports. This list is the ONLY thing that changed.

export const PORTAL_PRESETS: ReadonlyArray<{ value: ReportingDatePreset; label: string }> = [
  // Scott's eight, in his order (2026-09-02).
  { value: 'past_7', label: 'Past 7 days' },
  { value: 'past_14', label: 'Past 14 days' },
  { value: 'past_30', label: 'Past 30 days' },
  { value: 'past_60', label: 'Past 60 days' },
  { value: 'past_90', label: 'Past 90 days' },
  { value: 'this_month', label: 'This month so far' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom range…' },
  // ⚠️ THE THREE SCOTT DID NOT NAME, KEPT DELIBERATELY. Removing a period a
  // client may already be reading is a regression, and this is a dropdown
  // rather than a screen — the cost of three extra rows is nothing against the
  // cost of taking somebody's window away.
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'past_365', label: 'Past 12 months' },
  { value: 'this_year', label: 'This year' },
];

export function PeriodControl({
  preset,
  custom,
  onPresetChange,
  onCustomChange,
}: {
  preset: ReportingDatePreset;
  custom: Partial<DateRange>;
  onPresetChange: (preset: ReportingDatePreset) => void;
  onCustomChange: (range: Partial<DateRange>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SelectField
        aria-label="Reporting period"
        value={preset}
        onValueChange={(v) => onPresetChange(v as ReportingDatePreset)}
        options={PORTAL_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
      />
      {preset === 'custom' && (
        <DateRangePicker
          ariaLabel="Custom reporting period"
          from={custom.from}
          to={custom.to}
          // ⚠️ The half-open range is held by the CALLER and only committed once
          // both ends exist — echoing a placeholder end back into the picker
          // makes the second click restart the selection, leaving a range the
          // user can never finish. Same trap the reporting DateRangePicker
          // documents.
          onChange={(from, to) => onCustomChange({ from, to })}
        />
      )}
    </div>
  );
}
